import { LOAD_STATUS } from '@qcwp/common'
import { isArray } from '@qcwp/utils'
import type IndexVue from './index.vue'
import type { BrandResponse, ModelResponse, VenderSeriesResponse, VendorResponse } from '~~/src/server/modules/car/types'
import { LogicBrand } from '~~/src/logic/car/brand'
import { useCarStore } from '~~/src/store'

type CascaderPropsType = InstanceType<typeof IndexVue>['$props']
type CascaderEmitsType = InstanceType<typeof IndexVue>['$emit']
export interface CascaderSelectedType { brand: BrandResponse | null; series: CascaderSelectedSeries | null; model: ModelResponse | null }
export interface CascaderSelectedSeries extends VenderSeriesResponse, Omit<VendorResponse, 'carSeriesList' | 'id'> {}

export function useCascaderComponent(props: CascaderPropsType, emits: CascaderEmitsType) {
  const currentStep = ref(0)
  const isSelect = ref(false)
  const state = ref<LOAD_STATUS>(LOAD_STATUS.INIT)

  const selectedBrand = ref<BrandResponse | null>(null)
  const selectedSeries = ref<CascaderSelectedSeries | null>(null)
  const selectedModel = ref<ModelResponse | null>(null)

  function selectBrand(brand: BrandResponse) {
    selectedBrand.value = brand
    selectedSeries.value = null
    selectedModel.value = null
    changeStep(1)
  }
  function selectSeries(series: CascaderSelectedSeries) {
    selectedSeries.value = series
    selectedModel.value = null
    changeStep(2)
  }
  function selectModel(data: ModelResponse) {
    selectedModel.value = data
    changeStep(3)
  }
  function clear() {
    selectedBrand.value = null
    selectedSeries.value = null
    selectedModel.value = null
    changeStep(0)
  }
  function changeStep(step: number) {
    if (step >= (props.step || 3)) {
      submit()
      return
    }
    currentStep.value = step
  }
  function submit(close = true) {
    emits('selected', { brand: unref(selectedBrand), series: unref(selectedSeries), model: unref(selectedModel) })

    if (close)
      isSelect.value = !close

    // 是否选择后清空
    props.selectedClear && clear()
  }
  const { init: brandInit, loadStatus: brandInitStatus } = useCascaderBrand()
  const { init: seriesInit, loadStatus: seriesInitStatus } = useCascaderSeries()
  const { init: modelInit, loadStatus: modelInitStatus } = useCascaderModel()
  const initStatus = computed(() => {
    if (!props.lazy || !props.brandId || [LOAD_STATUS.END].includes(state.value))
      return true
    if (!props.seriesId)
      return brandInitStatus.value === LOAD_STATUS.END
    if (!props.modelId)
      return seriesInitStatus.value === LOAD_STATUS.END

    return modelInitStatus.value === LOAD_STATUS.END
  })
  async function initHandle() {
    if (props.brandId === selectedBrand.value?.id
      && props.seriesId === selectedSeries.value?.id
      && props.modelId === selectedModel.value?.id) {
      state.value = LOAD_STATUS.END
      return
    }

    clear()
    // 无品牌id，直接提交清空数据
    if (!props.brandId) {
      state.value = LOAD_STATUS.END
      return submit(false)
    }

    state.value = LOAD_STATUS.LOADING
    const step = props.step || 3
    const brand = await brandInit(props.brandId)
    brand && selectBrand(brand)

    if (props.seriesId && brand && step >= 2) {
      const series = await seriesInit(props.seriesId, props.brandId)
      series && selectSeries(series)

      // modelId === '-1' 是为自定义填写车型名称
      if (props.modelId && series && step === 3) {
        const model = await modelInit(props.modelId, props.seriesId)
        model && selectModel(model)
      }
    }
    state.value = LOAD_STATUS.END

    // ?? 为什么之前注释掉了
    submit(false)
  }

  let stops = [
    watch(
      () => [props.brandId, props.seriesId, props.modelId],
      initHandle,
      { immediate: !props.lazy },
    ),
    watch(isSelect, () => {
      if (!isSelect.value || !props.lazy)
        return

      initHandle()
    }, { immediate: true }),
  ]

  onUnmounted(() => {
    stops.forEach(stop => stop && stop())
    stops = null!
  })

  return {
    currentStep,
    initStatus,
    isSelect,
    state,
    selectedBrand,
    selectedSeries,
    selectedModel,

    selectBrand,
    selectSeries,
    selectModel,

    clear,
    changeStep,
    initHandle,
  }
}

export function useCascaderBrand() {
  const {
    brands,
    formatData: formatBrandsData,
    indexList: brandsIndexList,
    getBrandListData,
  } = LogicBrand()

  const loadStatus = ref<LOAD_STATUS>(LOAD_STATUS.INIT)
  async function getBrandList() {
    try {
      loadStatus.value = LOAD_STATUS.LOADING
      if (!brands.value.length)
        await getBrandListData()

      if (!brandsIndexList)
        throw new Error('load brand list error')
      loadStatus.value = LOAD_STATUS.END
    }
    catch (error) {
      loadStatus.value = LOAD_STATUS.ERROR
    }
  }
  async function init(brandId: string) {
    if (!brandId)
      return false
    await getBrandList()

    return brands.value.find(item => item.id === brandId) || false
  }

  return {
    brands,
    formatBrandsData,
    brandsIndexList,
    loadStatus,
    getBrandList,
    init,
  }
}

export function useCascaderSeries() {
  const carStore = useCarStore()
  const seriesList = ref<{ vender: VendorResponse[]; allSeries: VenderSeriesResponse[] } | null>(null)
  const loadStatus = ref<LOAD_STATUS>(LOAD_STATUS.INIT)
  async function getSeriesList(brandId: string) {
    try {
      loadStatus.value = LOAD_STATUS.LOADING
      await carStore.getSeriesList(brandId)
      seriesList.value = carStore.getCurrentBrandSeries(brandId)
      if (!seriesList.value?.allSeries)
        throw new Error('load model list error')

      loadStatus.value = LOAD_STATUS.END
    }
    catch (error) {
      loadStatus.value = LOAD_STATUS.ERROR
    }
  }
  async function init(id: string, brandId: string) {
    if (!brandId || !id)
      return false
    await getSeriesList(brandId)
    if (!seriesList.value || !isArray(seriesList.value?.allSeries))
      return false

    for (const vendor of seriesList.value.vender) {
      if (!isArray(vendor.carSeriesList))
        continue
      const series = vendor.carSeriesList.find(item => item.id === id)
      if (series) {
        return {
          ...series,
          carVendorName: vendor.carVendorName,
          carVendorId: vendor.carVendorId,
        }
      }
    }

    return false
  }

  return {
    seriesList,
    loadStatus,
    getSeriesList,
    init,
  }
}

export function useCascaderModel() {
  const carStore = useCarStore()
  const list = ref<{ count: number; years: string[]; modelsYears: Record<string, ModelResponse[]> } | null>(null)
  const loadStatus = ref<LOAD_STATUS>(LOAD_STATUS.INIT)
  async function getModelList(seriesId: string) {
    try {
      loadStatus.value = LOAD_STATUS.LOADING
      const result = await carStore.getModelList(seriesId)
      list.value = carStore.getCurrentSeriesModel(seriesId) || null
      loadStatus.value = result ? LOAD_STATUS.END : LOAD_STATUS.ERROR
    }
    catch (error) {
      console.error(error)
      loadStatus.value = LOAD_STATUS.ERROR
    }
  }

  async function init(id: string, seriesId: string) {
    if (!seriesId || !id)
      return false
    await getModelList(seriesId)

    if (!list.value || !list.value.modelsYears)
      return false

    for (const year in list.value.modelsYears) {
      const models = list.value.modelsYears[year]

      const model = models.find(item => item.id === id)
      if (model)
        return model
    }
    return false
  }
  return {
    list,
    loadStatus,
    getModelList,
    init,
  }
}
